/**
 * Settings panel — on-prem license activation (ADR-0073 slice 1d). Paste the
 * activation key → the hub calls the license server, stores the signed token,
 * and shows the resulting status. Gated `settings:read`; the Activate button is
 * gated `settings:write`. On the SaaS / dev this is inert (not enforced, no
 * license server) and the panel says so. Rendered as a panel inside the unified
 * Settings page.
 *
 * It ALSO shows what the licence actually GRANTS (features + caps + usage). A
 * licence panel that says only "active until <date>" leaves the operator unable
 * to answer "am I about to hit a cap?" — the exact question the caps exist to
 * make answerable — so the entitlements the hub is enforcing are rendered here,
 * read from the SAME `/entitlements` the enforcement seams resolve from (no
 * second source of truth, and no dependency on a licence server round-trip).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type Entitlements, entitlementsApi } from "@/lib/api-entitlements";
import { formatGb } from "@/lib/api-billing";
import { type LicenseStatus, licenseApi } from "@/lib/api-license";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";

const STATE_DOT: Record<string, string> = {
  active: "bg-success",
  grace: "bg-warning",
  expired: "bg-danger",
  invalid: "bg-danger",
  none: "bg-muted-foreground",
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * Axis label. The `/entitlements` payload carries KEYS only (the backend's own
 * English labels live in the plans catalog, which an on-prem install has no
 * route to), so the i18n map is the label source — with the raw key as the
 * last-resort fallback so a NEW backend axis renders as itself instead of
 * disappearing from a panel whose whole job is to be exhaustive.
 */
function useAxisLabel() {
  const { t } = useTranslation();
  return (key: string) => t(`billing.axis.${key}`, key);
}

function meterColor(ratio: number): string {
  if (ratio >= 1) return "bg-danger";
  if (ratio >= 0.8) return "bg-warning";
  return "bg-accent";
}

function LimitRow({ axis, limit, used }: { axis: string; limit: number | null; used: number | null }) {
  const { t } = useTranslation();
  const label = useAxisLabel()(axis);
  const unlimited = limit === null;
  // Storage is fractional GB — a sub-GB install must not read as a truncated "0".
  const fmt = (v: number) => (axis === "max_storage_gb" ? formatGb(v) : v.toLocaleString());
  const ratio = unlimited || used === null || limit === 0 ? 0 : Math.min(used / limit, 1);
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums text-foreground">
          <span className="font-medium">{used === null ? "—" : fmt(used)}</span>
          {unlimited ? (
            <span className="ml-1 rounded-full bg-elevated px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("billing.unlimited", "illimitato")}
            </span>
          ) : (
            <span className="text-muted-foreground"> / {fmt(limit)}</span>
          )}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-elevated">
        <div
          className={cn("h-full rounded-full transition-all", unlimited ? "bg-border" : meterColor(ratio))}
          style={{
            width: unlimited ? "100%" : `${Math.max(2, Math.round(ratio * 100))}%`,
            opacity: unlimited ? 0.4 : 1,
          }}
        />
      </div>
    </div>
  );
}

/**
 * What the licence GRANTS. Reads `/entitlements` — the same resolution the
 * enforcement seams use — so the panel can never claim a cap the runtime isn't
 * applying. Renders nothing when the install isn't licence-enforced (the SaaS /
 * dev path already has the Abbonamento panel) and nothing while unresolved: an
 * empty grid is worse than no grid.
 */
function LicenseEntitlements() {
  const { t } = useTranslation();
  const axisLabel = useAxisLabel();
  const q = useQuery<Entitlements>({
    queryKey: ["entitlements"],
    queryFn: () => entitlementsApi.get(),
    staleTime: 60_000,
    retry: false,
  });
  const ent = q.data;
  if (!ent) return null;

  const features = Object.entries(ent.features);
  const limits = Object.entries(ent.limits);

  return (
    <div className="rounded-lg border border-border bg-surface p-5">
      <h3 className="text-sm font-semibold text-foreground">
        {t("license.entitlements", "Cosa include questa licenza")}
      </h3>
      <p className="mb-4 mt-0.5 text-xs text-muted-foreground">
        {ent.unlimited
          ? t("license.entitlementsUnlimited", "Nessun limite applicato a questa installazione.")
          : t(
              "license.entitlementsHint",
              "Funzionalità e limiti applicati dal motore su questa installazione.",
            )}
      </p>

      {features.length > 0 && (
        <div className="mb-5">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("billing.features", "Funzionalità")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {features.map(([key, on]) => (
              <span
                key={key}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-xs",
                  on
                    ? "border-success/40 bg-success/10 text-foreground"
                    : "border-border bg-elevated text-muted-foreground line-through",
                )}
              >
                {axisLabel(key)}
              </span>
            ))}
          </div>
        </div>
      )}

      {limits.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("billing.usage", "Consumi")}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {limits.map(([key, lu]) => (
              <LimitRow key={key} axis={key} limit={lu.limit} used={lu.used} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function LicenseSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("settings", "write");

  const q = useQuery({ queryKey: ["license", "status"], queryFn: licenseApi.status });
  const [key, setKey] = React.useState("");

  const onStatus = (data: LicenseStatus) => qc.setQueryData(["license", "status"], data);

  const activate = useMutation({
    mutationFn: () => licenseApi.activate(key.trim()),
    onSuccess: (data) => {
      onStatus(data);
      setKey("");
    },
  });
  const heartbeat = useMutation({
    mutationFn: () => licenseApi.heartbeat(),
    onSuccess: onStatus,
  });

  if (q.isError) {
    return (
      <p className="text-sm text-danger">
        {t("license.loadError", "Impossibile caricare lo stato della licenza")}
      </p>
    );
  }
  const data = q.data;
  if (!data) {
    return <p className="text-sm text-muted-foreground">{t("common.loading", "Caricamento…")}</p>;
  }

  const dt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : t("license.never", "mai");
  const stateLabel = t(`license.state.${data.state}`, data.state);

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface p-5">
        <h3 className="text-sm font-semibold text-foreground">
          {t("license.title", "Licenza on-prem")}
        </h3>
        <p className="mb-4 mt-0.5 text-xs text-muted-foreground">
          {t(
            "license.subtitle",
            "Attiva l'installazione con la chiave ricevuta. Su cloud/prova questa sezione è inattiva.",
          )}
        </p>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-muted-foreground">{t("license.status", "Stato")}</span>
            <span className="flex items-center gap-2 font-medium text-foreground">
              <span className={cn("h-2 w-2 rounded-full", STATE_DOT[data.state] ?? "bg-muted-foreground")} />
              {data.activated
                ? stateLabel
                : t("license.notActivated", "Non attivata")}
            </span>
          </div>
          {data.organization && (
            <Field label={t("license.organization", "Organizzazione")} value={data.organization} />
          )}
          {data.expires_at && (
            <Field label={t("license.expiresAt", "Scadenza")} value={dt(data.expires_at)} />
          )}
          <Field label={t("license.heartbeatAt", "Ultimo heartbeat")} value={dt(data.heartbeat_at)} />
          {data.installation_id && (
            <Field label={t("license.installationId", "ID installazione")} value={data.installation_id} />
          )}
        </div>
      </div>

      {data.enforced && data.activated && <LicenseEntitlements />}

      {!data.enforced && !data.server_configured ? (
        <p className="text-xs text-muted-foreground">
          {t(
            "license.inactiveHint",
            "Il controllo licenza non è attivo su questa istanza (cloud o modalità sviluppo).",
          )}
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-surface p-5">
          <h3 className="text-sm font-semibold text-foreground">
            {t("license.activate", "Attiva con una chiave")}
          </h3>
          <p className="mb-4 mt-0.5 text-xs text-muted-foreground">
            {t("license.activateHint", "Incolla la chiave di attivazione (XXXX-XXXX-XXXX-XXXX-XXXX).")}
          </p>
          <div className="flex items-start gap-2">
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
              className="font-mono"
              disabled={!canWrite || activate.isPending}
              spellCheck={false}
            />
            <Button
              onClick={() => activate.mutate()}
              disabled={!canWrite || !key.trim() || activate.isPending}
            >
              {activate.isPending
                ? t("license.activating", "Attivazione…")
                : t("license.activate", "Attiva")}
            </Button>
          </div>
          {activate.isError && (
            <p className="mt-2 text-xs text-danger">
              {t("license.activateError", "Attivazione non riuscita. Controlla la chiave e riprova.")}
            </p>
          )}

          {data.activated && (
            <div className="mt-4 border-t border-border pt-4">
              <Button
                variant="outline"
                onClick={() => heartbeat.mutate()}
                disabled={!canWrite || heartbeat.isPending}
              >
                {heartbeat.isPending
                  ? t("license.refreshing", "Aggiornamento…")
                  : t("license.refresh", "Aggiorna licenza")}
              </Button>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("license.refreshHint", "Sincronizza con il server licenze (rinnovo, revoca, piano).")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
